import { uiText } from '../../../../utils/uiTranslation';
import React, { useState, useRef } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { ref, push, set } from 'firebase/database';
import { database } from '../../../../firebase';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../contexts/LanguageContext';
import nationalizationService, {
  announceNationalizationWorkforceChange
} from '../../../../services/nationalizationService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000" || 'http://localhost:8000';

const DOCUMENT_TYPES = [
  'contract',
  'resume',
  'id_card',
  'work_permit',
  'visa',
  'insurance',
  'tax_form',
  'nda',
  'policy',
  'other'
];

const UploadDocumentModal = ({ employeeId, companyUID, employeeName, onClose, onSuccess, initialDocType = 'other' }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL } = useLanguage();
  const [files, setFiles] = useState([]);
  const [docType, setDocType] = useState(initialDocType);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);
  const safeEmployeeName = employeeName || t('drawerModals.common.unknownEmployee', 'Unknown Employee');

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      alert(t('drawerModals.upload.errors.noFiles', 'Please select at least one file'));
      return;
    }

    setUploading(true);
    const progress = {};
    files.forEach((_, i) => progress[i] = 0);
    setUploadProgress(progress);

    try {
      if (!currentUser) throw new Error(t('drawerModals.upload.errors.signInRequired', 'Sign in before uploading documents.'));
      // The backend derives identity and tenant permissions from this token. It
      // deliberately ignores actor names or UIDs supplied by the browser.
      const idToken = await currentUser.getIdToken();
      const uploadPromises = files.map(async (file, index) => {
        try {
          // Upload file to backend (server handles encryption)
          const formData = new FormData();
          formData.append('file', file);
          formData.append('companyUID', companyUID);
          formData.append('employeeId', employeeId);
          formData.append('docType', docType);
          formData.append('notes', notes);
          formData.append('clientEncrypted', 'false');

          const xhr = new XMLHttpRequest();

          return new Promise((resolve, reject) => {
            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                const uploadPercent = Math.round((e.loaded / e.total) * 100);
                setUploadProgress(prev => ({ ...prev, [index]: uploadPercent }));
              }
            });

            xhr.addEventListener('load', () => {
              if (xhr.status === 200) {
                const result = JSON.parse(xhr.responseText || '{}');
                if (result.success === false) {
                  reject(new Error(result.error || t('drawerModals.upload.errors.failureShort', 'Upload failed')));
                } else {
                  resolve(result);
                }
              } else {
                const errorData = JSON.parse(xhr.responseText || '{}');
                reject(new Error(errorData.error || `Upload failed: ${xhr.statusText}`));
              }
            });

            xhr.addEventListener('error', () => reject(new Error('Upload failed')));

            xhr.open('POST', `${BACKEND_URL}/employee/upload-document-secure`);
            xhr.setRequestHeader('Authorization', `Bearer ${idToken}`);
            xhr.send(formData);
          });
        } catch (error) {
          console.error(`Failed to upload file ${file.name}:`, error);
          throw error;
        }
      });

      const uploadResults = await Promise.all(uploadPromises);

      // The backend commits regulatory documents with durable lifecycle markers.
      // Try them now; failed delivery remains queued for authorized replay.
      const lifecycleEvents = uploadResults
        .map((result) => result?.nationalizationLifecycleEvent)
        .filter(Boolean);
      let recalculated = false;
      let syncPending = false;
      for (const event of lifecycleEvents) {
        try {
          const syncResult = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
            eventId: event.eventId,
            source: event.source,
            changeKinds: event.changeKinds
          });
          recalculated = recalculated || syncResult?.recalculated === true;
        } catch (syncError) {
          syncPending = true;
          console.warn('Nationalization document review remains queued:', syncError);
        }
      }
      if (lifecycleEvents.length) {
        announceNationalizationWorkforceChange({
          companyUID,
          source: 'employee_regulatory_document_upload',
          recalculated,
          syncPending
        });
      }

      // Send Notification
      try {
        const notifRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`));
        await set(notifRef, {
          title: t('drawerModals.upload.notificationTitle', 'Document uploaded'),
          message: t('drawerModals.upload.notificationMessage', {
            count: files.length,
            actor: currentUser?.displayName || t('drawerModals.upload.hrTeam', 'HR team'),
            defaultValue: '{{count}} document(s) were uploaded to your file by {{actor}}.'
          }),
          type: 'info',
          timestamp: Date.now(),
          read: false
        });
      } catch (err) { console.error(err); }

      alert(t('drawerModals.upload.success', {
        count: files.length,
        defaultValue: `Successfully uploaded ${files.length} document${files.length !== 1 ? 's' : ''}`
      }));
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Upload failed:', error);
      alert(t('drawerModals.upload.errors.failure', {
        message: error.message || t('drawerModals.upload.title', 'Upload Document')
      }));
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '600px',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left'
        }}
      >
        <div className="modal-header">
          <h2>
            {files.length > 1
              ? t('drawerModals.upload.titlePlural', 'Upload Documents')
              : t('drawerModals.upload.title', 'Upload Document')}
          </h2>
          <button className="close" onClick={onClose}>&times;</button>
        </div>
        <div className="form-section">
          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.common.employee', 'Employee')}
            </label>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              color: 'var(--app-heading, #031128)'
            }}>
              {safeEmployeeName}
            </div>
          </div>

          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.upload.documentType', 'Document Type')}
            </label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)',
                backgroundColor: '#FFFFFF',
                cursor: 'pointer'
              }}
            >
              {DOCUMENT_TYPES.map(type => (
                <option key={type} value={type}>
                  {t(`documents.types.${type}`, type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()))}
                </option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.common.files', 'Files')}
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed #E0E0E0',
                borderRadius: '8px',
                padding: '40px 20px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: '#F8F9FA'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#568ded';
                e.currentTarget.style.backgroundColor = '#F0F7FF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#E0E0E0';
                e.currentTarget.style.backgroundColor = '#F8F9FA';
              }}
             >
              <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '32px', color: '#568ded', marginBottom: '12px' }}></i>
              <div style={{ fontFamily: 'Montserrat', fontSize: '16px', color: 'var(--app-heading, #031128)', fontWeight: 600 }}>
                {t('drawerModals.common.dropHint', 'Click to select files or drag and drop')}
              </div>
              <div style={{ fontFamily: 'Montserrat', fontSize: '14px', color: '#666', marginTop: '4px' }}>
                {t('drawerModals.common.fileTypes', 'PDF, DOC, DOCX, JPG, PNG (Max 10MB per file)')}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
          </div>

          {files.length > 0 && (
            <div className="input-group">
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--app-heading, #031128)'
              }}>
                {t('drawerModals.upload.selectedFiles', { count: files.length, defaultValue: `Selected Files (${files.length})` })}
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {files.map((file, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: '#F8F9FA',
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Montserrat', fontSize: '14px', color: 'var(--app-heading, #031128)', fontWeight: 600 }}>
                        {file.name}
                      </div>
                      <div style={{ fontFamily: 'Montserrat', fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                        {uploadProgress[index] !== undefined && uiText(" • {{percent}}% uploaded", { percent: uploadProgress[index] })}
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--app-tone-eb4040, #EB4040)',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        fontSize: '18px',
                        padding: '4px 8px'
                      }}
                      data-light-control={"quiet-danger"}>
                      <i className="fa-solid fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.common.notesOptional', 'Notes (Optional)')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('drawerModals.upload.notesPlaceholder', 'Add any notes about these documents...')}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                color: 'var(--app-heading, #031128)',
                resize: 'vertical'
              }}
            />
          </div>
        </div>
        <div className="modal-footer" style={{
          padding: '20px 32px',
          borderTop: '1px solid #E0E0E0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <button
            className="btn-secondary"
            onClick={onClose}
            disabled={uploading}
            style={{
              padding: '12px 24px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: "var(--app-neutral-ink, #031128)",
              cursor: uploading ? 'not-allowed' : 'pointer'
            }}
            data-light-control={"quiet"}>
            {t('common:buttons.cancel', 'Cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={handleUpload}
            disabled={uploading || files.length === 0}
            style={{ '--light-control-fill': uploading || files.length === 0 ? '#CCCCCC' : '#568ded',
              padding: '12px 24px',
              backgroundColor: uploading || files.length === 0 ? '#CCCCCC' : '#568ded',
              border: 'none',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: '#FFFFFF',
              cursor: uploading || files.length === 0 ? 'not-allowed' : 'pointer'
            }}
            data-light-control={uploading || files.length === 0 ? "quiet" : "filled"}>
            {uploading
              ? t('drawerModals.upload.uploading', 'Uploading...')
              : `${files.length > 1
                ? t('drawerModals.upload.titlePlural', 'Upload Documents')
                : t('drawerModals.upload.title', 'Upload Document')}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDocumentModal;
